import { BaseElement } from 'base';
import { bus } from 'bus';
import { cart } from 'cart';
import { addItem, cartExtras } from 'cart-extras';
import { pdpProperties } from 'amplify-bundles';
import { productVariant } from 'product-variant';
import { sectionFragment } from 'section-fragment';
import {
  SHOP_THE_LOOK_TOPIC,
  SHOP_THE_LOOK_REQUEST_TOPIC
} from 'shop-the-look-topic';
import { SLIDE_CART_TOPIC, CART_DRAWER_ID } from 'slide-cart-topic';
import { NOTIFY_MODAL_TOPIC } from 'notify-modal-topic';
import { SHOP_THE_LOOK_FRAGMENT_SECTION as FRAGMENT_SECTION } from 'section-ids';

const MODAL_TAG = 'shop-the-look-modal';
const SLOT = '[data-stl-slot]';
const FRAGMENT_ID = 'shop-the-look-fragment';
const STYLE_WAIT_MS = 1500;
const NOTIFY_FROM = 'shop_my_look_details';

class ShopTheLookModals extends BaseElement {
  #active = null;
  #request = null;
  #epoch = 0;
  #showing = 0;
  #cartAfterClose = false;
  #modals = new Map();
  #styles = new Map();
  #styleWaiters = new Map();

  onConnect() {
    this.#active = null;
    this.#request = null;
    this.#epoch += 1;
    this.#cartAfterClose = false;
    this.#modals = new Map();
    this.#styles = new Map();
    this.#styleWaiters = new Map();

    for (const slot of this.qa(SLOT)) {
      const productId = Number(slot.dataset.productId);
      const modal = this.q(`#${CSS.escape(slot.dataset.stlSlot)}`);
      if (!productId || !modal || this.#modals.has(productId)) continue;
      this.#modals.set(productId, { modal, slot, ready: Promise.resolve() });
    }

    this.listen(this, 'load', this.#onStyleSettled, true);
    this.listen(this, 'error', this.#onStyleSettled, true);
    this.cleanup(() => this.#styleWaiters.clear());
    this.subscribe(SHOP_THE_LOOK_TOPIC, this.#onActive);
    this.subscribe(SHOP_THE_LOOK_REQUEST_TOPIC, this.#onRequest);

    const epoch = this.#epoch;
    const rendered = [...this.#modals.values()].map(({ modal }) => modal);
    customElements.whenDefined(MODAL_TAG).then(() => {
      if (!this.isConnected || epoch !== this.#epoch) return;
      for (const modal of rendered) this.#bind(modal);
    });
  }

  #bind(modal) {
    modal.run(request => this.#act(modal, request));
  }

  #onActive(value) {
    const was = this.#active;
    this.#active = value ?? null;
    if (this.#active) {
      this.#showing += 1;
      if (this.#active !== was) this.#cartAfterClose = false;
    }
    if (was && !this.#active) queueMicrotask(() => this.#settle());
  }

  #settle() {
    if (!this.#active && this.#request?.status === 'open')
      bus.publish(SHOP_THE_LOOK_REQUEST_TOPIC, null);
    if (!this.#cartAfterClose) return;
    this.#cartAfterClose = false;
    bus.publish(SLIDE_CART_TOPIC, CART_DRAWER_ID);
  }

  #onRequest(request) {
    const previous = this.#request;
    this.#request = request ?? null;
    if (request?.status !== 'pending' || request.id === previous?.id) return;
    this.#answer(request);
  }

  async #answer(request) {
    const { id, productId, url } = request;
    const epoch = this.#epoch;
    const isStale = () => epoch !== this.#epoch || this.#request?.id !== id;
    try {
      const entry =
        this.#modals.get(productId) ?? (await this.#load(productId, url));
      if (!entry) return;
      if (await this.#show(entry, isStale))
        bus.publish(SHOP_THE_LOOK_REQUEST_TOPIC, {
          ...request,
          status: 'open'
        });
    } catch (error) {
      if (isStale()) return;
      console.error('[shop-the-look-modals] request failed:', error);
      bus.publish(SHOP_THE_LOOK_REQUEST_TOPIC, {
        ...request,
        status: 'failed'
      });
    }
  }

  async #add({ variantId, quantity, preOrder, extras = [], lines = [] }) {
    const trigger = {
      id: variantId,
      quantity,
      properties: pdpProperties(preOrder)
    };
    const bundle = await cartExtras.lines(lines);
    if (bundle?.items.length) return cartExtras.replace(trigger, bundle);
    if (bundle) return addItem(trigger);

    const found = await cartExtras.collect(extras);
    if (cartExtras.isEmpty(found))
      return cart.add({
        id: variantId,
        quantity,
        properties: preOrder ? { _pre_order: 'true' } : undefined
      });
    return cartExtras.add(trigger, found);
  }

  async #act(modal, request) {
    switch (request?.type) {
      case 'add': {
        const showing = this.#showing;
        await this.#add(request);
        if (showing !== this.#showing) return;
        if (this.#active === modal.id) this.#cartAfterClose = true;
        else bus.publish(SLIDE_CART_TOPIC, CART_DRAWER_ID);
        return;
      }
      case 'color':
        await this.#swap(modal, request);
        return;
      case 'notify':
        this.#notify(request);
        return;
      case 'prefetch':
        if (request.url && !this.#modals.has(request.productId))
          productVariant
            .sections(request.url, FRAGMENT_SECTION)
            .catch(() => {});
        return;
      default:
        throw new Error(
          `[shop-the-look-modals] unknown action: ${request?.type}`
        );
    }
  }

  async #swap(current, { productId, url }) {
    const showing = this.#showing;
    const entry =
      this.#modals.get(productId) ?? (await this.#load(productId, url));
    if (!entry) return;
    await this.#show(
      entry,
      () =>
        showing !== this.#showing ||
        this.#active !== current.id ||
        entry.modal === current
    );
  }

  async #show(entry, isStale) {
    await entry.ready;
    if (!this.isConnected || isStale()) return false;
    if (!customElements.get(MODAL_TAG))
      throw new Error('[shop-the-look-modals] modal element is not defined');

    bus.publish(SHOP_THE_LOOK_TOPIC, null);
    bus.publish(SHOP_THE_LOOK_TOPIC, entry.modal.id);
    entry.slot.append(entry.modal);
    if (!entry.slot.isConnected) this.append(entry.slot);
    this.#bind(entry.modal);
    return true;
  }

  async #load(productId, url) {
    const sections = await productVariant.sections(url, FRAGMENT_SECTION);
    const id = `${FRAGMENT_ID}-${productId}`;
    const root = sectionFragment(sections, FRAGMENT_SECTION, `${id}-root`);
    const fresh = root?.querySelector(`#${CSS.escape(id)}`);
    if (!fresh) {
      throw new Error(
        `[shop-the-look-modals] no modal in the fragment for product ${productId}`
      );
    }
    if (!this.isConnected) return null;

    const known = this.#modals.get(productId);
    if (known) return known;

    const links = [];
    const styles = [];
    for (const link of root.querySelectorAll("link[rel='stylesheet']")) {
      const href = link.getAttribute('href');
      if (href && !this.#styles.has(href)) {
        this.#styles.set(href, this.#styleSettled(link));
        links.push(link);
      } else {
        link.remove();
      }
      if (href) styles.push(this.#styles.get(href));
    }
    const scripts = [
      ...root.querySelectorAll("script[type='module'][src]")
    ].map(script => new URL(script.getAttribute('src'), document.baseURI).href);

    const slot = document.createElement('div');
    slot.className = 'shop-the-look-modals__slot';
    slot.dataset.stlSlot = fresh.id;
    slot.dataset.productId = String(productId);
    slot.append(...links);
    this.append(slot);

    const entry = {
      modal: fresh,
      slot,
      ready: this.#activate(scripts, styles)
    };
    this.#modals.set(productId, entry);
    return entry;
  }

  async #activate(scripts, styles) {
    await Promise.all([
      ...scripts.map(src =>
        import(src).catch(error =>
          console.error(
            '[shop-the-look-modals] module failed to load:',
            src,
            error
          )
        )
      ),
      this.#stylesReady(styles)
    ]);
  }

  #styleSettled(link) {
    return new Promise(resolve => this.#styleWaiters.set(link, resolve));
  }

  #stylesReady(styles) {
    if (styles.length === 0) return Promise.resolve();
    let timer;
    const timeout = new Promise(resolve => {
      timer = setTimeout(resolve, STYLE_WAIT_MS);
    });
    return Promise.race([Promise.all(styles), timeout]).finally(() =>
      clearTimeout(timer)
    );
  }

  #onStyleSettled(event) {
    const resolve = this.#styleWaiters.get(event.target);
    if (!resolve) return;
    this.#styleWaiters.delete(event.target);
    resolve();
  }

  #notify({ productId, variantId, handle }) {
    bus.publish(NOTIFY_MODAL_TOPIC, {
      productId,
      variantId,
      handle: handle ?? '',
      from: NOTIFY_FROM
    });
  }
}

if (!customElements.get('shop-the-look-modals')) {
  customElements.define('shop-the-look-modals', ShopTheLookModals);
}
